/**
 * Sector Topics Panel
 *
 * Displays and filters sector topics with rich attribute metadata
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { authFetch } from '@/utils/auth-fetch';
import {
  Loader2,
  Database,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  BarChart3,
  Tag,
  Download,
  Trash2,
  Calendar,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface Topic {
  id: number;
  topic_name: string;
  topic_description: string;
  status: string;
  created_from: string;
  attribute_source?: string;
  product_count?: number;
  value_distribution?: Record<string, number>;
  top_categories?: Array<{ category: string; count: number }>;
  has_research: boolean;
  created_at: string;
  sector_name?: string;
  site_domain?: string;
}

interface DataSource {
  data_source_id: string;
  data_source_name: string;
  topic_count: number;
}

interface SectorTopicsPanelProps {
  siteId: string;
  sectorId?: number | null;
}

export function SectorTopicsPanel({ siteId, sectorId }: SectorTopicsPanelProps) {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [createdFromFilter, setCreatedFromFilter] = useState<string>('all');
  const [researchFilter, setResearchFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dataSourceFilter, setDataSourceFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [selectedTopics, setSelectedTopics] = useState<Set<number>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    loadTopics();
    loadDataSources();
  }, [sectorId]);

  // Reload topics when filters change
  useEffect(() => {
    if (sectorId) {
      loadTopics();
    }
  }, [dataSourceFilter, dateFromFilter, dateToFilter, statusFilter, createdFromFilter]);

  const loadDataSources = async () => {
    if (!siteId) return;

    try {
      const response = await authFetch(`/qa/topics/data-sources?siteId=${siteId}`);
      const data = await response.json();

      if (data.success) {
        setDataSources(data.dataSources || []);
      }
    } catch (error) {
      console.error('Failed to load data sources:', error);
    }
  };

  const loadTopics = async () => {
    if (!sectorId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        siteId,
        sectorId: sectorId.toString(),
        limit: '1000'
      });

      if (dataSourceFilter !== 'all') {
        params.append('dataSourceId', dataSourceFilter);
      }
      if (dateFromFilter) {
        params.append('dateFrom', dateFromFilter);
      }
      if (dateToFilter) {
        params.append('dateTo', dateToFilter);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (createdFromFilter !== 'all') {
        params.append('createdFrom', createdFromFilter);
      }

      const response = await authFetch(`/qa/topics/list?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setTopics(data.topics || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load topics:', error);
      toast({
        title: "Error",
        description: "Failed to load sector topics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filter (for search box)
  const filteredTopics = topics.filter(topic => {
    // Search filter
    if (searchQuery && !topic.topic_name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Research filter (client-side only)
    if (researchFilter === 'with_research' && !topic.has_research) {
      return false;
    }
    if (researchFilter === 'without_research' && topic.has_research) {
      return false;
    }

    return true;
  });

  const handleBulkDelete = async () => {
    if (selectedTopics.size === 0) {
      toast({
        title: "No topics selected",
        description: "Please select topics to delete",
        variant: "destructive"
      });
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedTopics.size} topic(s)? This will also delete associated research and questions.`;
    if (!confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await authFetch('/qa/topics/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topicIds: Array.from(selectedTopics),
          deleteResearch: true,
          deleteQuestions: true
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Topics deleted",
          description: `Successfully deleted ${data.deleted.topics} topics, ${data.deleted.research} research entries, ${data.deleted.questions} questions`,
        });

        // Clear selection and reload
        setSelectedTopics(new Set());
        await loadTopics();
      } else {
        throw new Error(data.error || 'Failed to delete topics');
      }
    } catch (error: any) {
      console.error('Failed to delete topics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete topics",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleTopicSelection = (topicId: number) => {
    const newSelection = new Set(selectedTopics);
    if (newSelection.has(topicId)) {
      newSelection.delete(topicId);
    } else {
      newSelection.add(topicId);
    }
    setSelectedTopics(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedTopics.size === filteredTopics.length) {
      setSelectedTopics(new Set());
    } else {
      setSelectedTopics(new Set(filteredTopics.map(t => t.id)));
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any; label: string }> = {
      'approved': {
        variant: 'default' as const,
        icon: CheckCircle,
        label: 'Approved'
      },
      'pending_approval': {
        variant: 'secondary' as const,
        icon: Clock,
        label: 'Pending Approval'
      },
      'pending_research': {
        variant: 'outline' as const,
        icon: AlertCircle,
        label: 'Pending Research'
      }
    };

    const config = variants[status] || variants['pending_approval'];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const headers = [
      'Topic Name',
      'Description',
      'Status',
      'Created From',
      'Has Research',
      'Attribute Source',
      'Product Count',
      'Value Distribution',
      'Top Categories',
      'Created At'
    ];

    const rows = filteredTopics.map(topic => [
      topic.topic_name,
      topic.topic_description,
      topic.status,
      topic.created_from,
      topic.has_research ? 'Yes' : 'No',
      topic.attribute_source || '',
      topic.product_count || '',
      topic.value_distribution
        ? Object.entries(topic.value_distribution)
            .map(([k, v]) => `${k}:${v}%`)
            .join('; ')
        : '',
      topic.top_categories
        ? topic.top_categories.map(c => `${c.category}(${c.count})`).join('; ')
        : '',
      new Date(topic.created_at).toISOString().split('T')[0]
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell =>
          // Escape commas and quotes in cell content
          typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))
            ? `"${cell.replace(/"/g, '""')}"`
            : cell
        ).join(',')
      )
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sector-topics-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "CSV Exported",
      description: `Exported ${filteredTopics.length} topics to CSV`,
    });
  };

  if (!sectorId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            This site needs to be assigned to a sector to view topics
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Topic Filters</CardTitle>
              <CardDescription>
                Filter and search through {totalCount} topics
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {selectedTopics.size > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  variant="destructive"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected ({selectedTopics.size})
                    </>
                  )}
                </Button>
              )}
              <Button
                onClick={exportToCSV}
                variant="outline"
                disabled={filteredTopics.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export to CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* First Row: Search, Data Source, Created From, Status */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Data Source Filter */}
            <Select value={dataSourceFilter} onValueChange={setDataSourceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Data Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Data Sources</SelectItem>
                {dataSources.map(ds => (
                  <SelectItem key={ds.data_source_id} value={ds.data_source_id}>
                    {ds.data_source_name || ds.data_source_id.substring(0, 8)} ({ds.topic_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Created From */}
            <Select value={createdFromFilter} onValueChange={setCreatedFromFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Created From" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="attribute_analysis">Attribute Analysis</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="google_search">Google Search</SelectItem>
              </SelectContent>
            </Select>

            {/* Status */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second Row: Date Range, Research Filter */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Date From */}
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                placeholder="From date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Date To */}
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                placeholder="To date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Has Research */}
            <Select value={researchFilter} onValueChange={setResearchFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Research Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                <SelectItem value="with_research">With Research</SelectItem>
                <SelectItem value="without_research">Without Research</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {filteredTopics.length} of {topics.length} topics</span>
            {selectedTopics.size > 0 && (
              <span className="text-primary font-medium">{selectedTopics.size} selected</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Topics List */}
      <div className="space-y-4">
        {/* Select All Checkbox */}
        {filteredTopics.length > 0 && (
          <Card className="bg-muted/50">
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedTopics.size === filteredTopics.length && filteredTopics.length > 0}
                  onCheckedChange={toggleSelectAll}
                  id="select-all"
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  Select all {filteredTopics.length} topics
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {filteredTopics.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">
                No topics match your filters
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredTopics.map((topic) => (
            <Card key={topic.id} className={selectedTopics.has(topic.id) ? 'ring-2 ring-primary' : ''}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  {/* Selection Checkbox */}
                  <div className="pt-1">
                    <Checkbox
                      checked={selectedTopics.has(topic.id)}
                      onCheckedChange={() => toggleTopicSelection(topic.id)}
                      id={`topic-${topic.id}`}
                    />
                  </div>

                  <div className="flex items-start justify-between flex-1">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{topic.topic_name}</CardTitle>
                      <CardDescription>{topic.topic_description}</CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {getStatusBadge(topic.status)}
                      {topic.has_research && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <FileText className="h-3 w-3 mr-1" />
                          Researched
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              {/* Attribute Metadata (only for attribute-based topics) */}
              {topic.created_from === 'attribute_analysis' && (
                <CardContent className="space-y-4">
                  {/* Attribute Source */}
                  {topic.attribute_source && (
                    <div className="flex items-center gap-2 text-sm">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Triggered by:</span>
                      <Badge variant="outline">{topic.attribute_source}</Badge>
                    </div>
                  )}

                  {/* Product Count */}
                  {topic.product_count && (
                    <div className="flex items-center gap-2 text-sm">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Products:</span>
                      <span className="font-medium">{topic.product_count.toLocaleString()}</span>
                    </div>
                  )}

                  {/* Value Distribution */}
                  {topic.value_distribution && Object.keys(topic.value_distribution).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Top Values:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(topic.value_distribution)
                          .slice(0, 10)
                          .map(([value, percentage]) => {
                            // Handle display of "0" or empty string values
                            const displayValue = value === '0' || value === '' ? '(empty/null)' : value;
                            // Percentage is already a number, format it properly
                            const displayPercentage = typeof percentage === 'number'
                              ? percentage.toFixed(1)
                              : percentage;
                            return (
                              <Badge key={value} variant="secondary" className="text-xs">
                                {displayValue}: {displayPercentage}%
                              </Badge>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Top Categories */}
                  {topic.top_categories && topic.top_categories.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Main Categories:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {topic.top_categories.slice(0, 5).map((cat, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {cat.category} ({cat.count})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}

              {/* Regular topic (no attribute metadata) */}
              {topic.created_from !== 'attribute_analysis' && (
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    Created from: <Badge variant="outline">{topic.created_from}</Badge>
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
